import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  Autocomplete,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';

export default function ServerOperatingSystemForm({ isEdit = false }) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper(true);
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper(false);
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    fetchServerEnvironmentData,
    masterData,
    validateStep
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 削除確認ダイアログの状態
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });
  // 新しく追加された要素へのスクロール用ref
  const formRefs = useRef([]);
  // 各カテゴリーのサービス選択肢を保持
  const [environmentOptions, setEnvironmentOptions] = useState({});
  // 各カテゴリーのサービス入力値を保持
  const [environmentInputValues, setEnvironmentInputValues] = useState({});

  // フォームの値を監視
  const server_operating_systems = watch('server_operating_systems') || [{ server_operating_system_name: '', server_operating_system_id: '', server_environment_name: '', server_environment_id: '' }];

  // 既に選択済みのカテゴリーを取得する関数
  const getSelectedServerOperatingSystems = useCallback(() => {
    return server_operating_systems
      .map(system => system.server_operating_system_name)
      .filter(name => name && name.trim() !== '');
  }, [server_operating_systems]);

  // 既に選択済みのサービスを取得する関数
  const getSelectedEnvironments = useCallback((currentIndex) => {
    const allEnvironments = [];
    server_operating_systems.forEach((system, index) => {
      if (index !== currentIndex && system.server_environment_name) {
        const environments = system.server_environment_name.split(', ').filter(f => f.trim() !== '');
        allEnvironments.push(...environments);
      }
    });
    return allEnvironments;
  }, [server_operating_systems]);

  // カテゴリーの選択肢をフィルタリングする関数
  const getFilteredServerOperatingSystemOptions = useCallback((currentIndex) => {
    const selectedSystems = getSelectedServerOperatingSystems();
    const currentSystem = server_operating_systems[currentIndex]?.server_operating_system_name;

    return (masterData.server_operating_systems || []).filter(system => {
      const systemName = typeof system === 'string' ? system : system.name;
      return !selectedSystems.includes(systemName) || systemName === currentSystem;
    });
  }, [masterData.server_operating_systems, getSelectedServerOperatingSystems, server_operating_systems]);

  // サービスの選択肢をフィルタリングする関数
  const getFilteredEnvironmentOptions = useCallback((currentIndex) => {
    const selectedEnvironments = getSelectedEnvironments(currentIndex); // 他のフォームで選択済み
    const currentEnvironments = server_operating_systems[currentIndex]?.server_environment_name 
      ? server_operating_systems[currentIndex].server_environment_name.split(', ').filter(f => f.trim() !== '')
      : []; // 現在のフォームで選択済み

    // 全ての選択済みサービスを結合
    const allSelectedEnvironments = [...selectedEnvironments, ...currentEnvironments];

    return (environmentOptions[currentIndex] || []).filter(env => {
      const envName = typeof env === 'string' ? env : env.name;
      return !allSelectedEnvironments.includes(envName);
    });
  }, [environmentOptions, getSelectedEnvironments, server_operating_systems]);

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData('server_operating_systems');
  }, [fetchMasterData]);

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // server_operating_systemsのバリデーション
    server_operating_systems.forEach((system, index) => {
      const hasServerOperatingSystemName = system.server_operating_system_name && system.server_operating_system_name !== '';
      const hasEnvironmentName = system.server_environment_name && system.server_environment_name !== '';

      // カテゴリーが入力されている場合、サービスも必須にする
      if (hasServerOperatingSystemName && !hasEnvironmentName) {
        setError(`server_operating_systems.${index}.server_environment_name`, {
          type: 'required',
          message: 'サービス名は入力必須です'
        });
        hasError = true;
      }

      // サービスが入力されている場合、カテゴリーも必須にする
      if (hasEnvironmentName && !hasServerOperatingSystemName) {
        setError(`server_operating_systems.${index}.server_operating_system_name`, {
          type: 'required',
          message: 'カテゴリーを入力してください'
        });
        hasError = true;
      }
    });

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(11, validateForm); // 12ステップ目（0ベースで11）
    }
  }, [validateStep, server_operating_systems]);

  // カテゴリー選択の変更ハンドラ
  const handleServerOperatingSystemChange = async (index, selectedSystem) => {
    const newSystems = [...server_operating_systems];

    if (typeof selectedSystem === 'string') {
      // 手動入力の場合
      newSystems[index] = {
        ...newSystems[index],
        server_operating_system_name: selectedSystem,
        server_operating_system_id: '',
        // カテゴリーが変更されたらサービスをクリア
        server_environment_name: '',
        server_environment_id: ''
      };
    } else if (selectedSystem && selectedSystem.name) {
      // オートコンプリートから選択した場合
      newSystems[index] = {
        ...newSystems[index],
        server_operating_system_name: selectedSystem.name,
        server_operating_system_id: selectedSystem.id || '',
        // カテゴリーが変更されたらサービスをクリア
        server_environment_name: '',
        server_environment_id: ''
      };

      // 選択したカテゴリーに基づいてサービスの選択肢を取得
      try {
        const environmentData = await fetchServerEnvironmentData(selectedSystem.name);
        setEnvironmentOptions(prev => ({
          ...prev,
          [index]: environmentData
        }));
      } catch (error) {
        console.error('Error fetching environment options:', error);
      }
    } else if (selectedSystem === null || selectedSystem === '') {
      // クリアされた場合
      newSystems[index] = {
        ...newSystems[index],
        server_operating_system_name: '',
        server_operating_system_id: '',
        server_environment_name: '',
        server_environment_id: ''
      };
      // サービスの選択肢もクリア
      setEnvironmentOptions(prev => ({
        ...prev,
        [index]: []
      }));
    }

    setValue('server_operating_systems', newSystems);

    // サービスの入力値もクリア
    setEnvironmentInputValues(prev => ({
      ...prev,
      [index]: ''
    }));

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.server_operating_systems;
    if (fieldErrors && fieldErrors[index]) {
      if (fieldErrors[index].server_operating_system_name) {
        clearErrors(`server_operating_systems.${index}.server_operating_system_name`);
      }
      if (fieldErrors[index].server_environment_name) {
        clearErrors(`server_operating_systems.${index}.server_environment_name`);
      }
    }
  };

  // サービス選択の変更ハンドラ
  const handleEnvironmentChange = (index, newValue) => {
    const newSystems = [...server_operating_systems];

    // 選択されたサービスを文字列に変換
    const environmentNames = newValue.map(item =>
      typeof item === 'string' ? item : item.name || ''
    ).join(', ');

    newSystems[index] = {
      ...newSystems[index],
      server_environment_name: environmentNames
    };
    setValue('server_operating_systems', newSystems);

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.server_operating_systems;
    if (fieldErrors && fieldErrors[index] && fieldErrors[index].server_environment_name) {
      clearErrors(`server_operating_systems.${index}.server_environment_name`);
    }
  };

  // サービス入力値変更ハンドラ
  const handleEnvironmentInputChange = (index, newInputValue) => {
    setEnvironmentInputValues(prev => ({
      ...prev,
      [index]: newInputValue
    }));
  };

  // サービスの手動入力確定
  const handleEnvironmentBlur = (index) => {
    const inputValue = environmentInputValues[index];
    if (inputValue && inputValue.trim() !== '') {
      const currentEnvironments = server_operating_systems[index].server_environment_name
        ? server_operating_systems[index].server_environment_name.split(', ').filter(f => f.trim() !== '')
        : [];

      const trimmedInput = inputValue.trim();
      if (!currentEnvironments.includes(trimmedInput)) {
        const newEnvironments = [...currentEnvironments, trimmedInput];
        const newSystems = [...server_operating_systems];
        newSystems[index] = {
          ...newSystems[index],
          server_environment_name: newEnvironments.join(', ')
        };
        setValue('server_operating_systems', newSystems);
      }

      // 入力値をクリア
      setEnvironmentInputValues(prev => ({
        ...prev,
        [index]: ''
      }));
    }
  };

  // 要素を追加
  const handleAdd = () => {
    const newSystems = [...server_operating_systems, { server_operating_system_name: '', server_operating_system_id: '', server_environment_name: '', server_environment_id: '' }];
    setValue('server_operating_systems', newSystems);

    // 新しい要素にスクロール
    setTimeout(() => {
      const newIndex = newSystems.length - 1;
      if (formRefs.current[newIndex]) {
        formRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 削除前の確認
  const handleDeleteConfirm = (index) => {
    const system = server_operating_systems[index];
    const hasContent = (system.server_operating_system_name && system.server_operating_system_name !== '') ||
                      (system.server_environment_name && system.server_environment_name !== '');

    if (hasContent) {
      setDeleteConfirmDialog({ open: true, index });
    } else {
      handleDelete(index);
    }
  };

  // 要素を削除
  const handleDelete = (index) => {
    const newSystems = server_operating_systems.filter((_, i) => i !== index);
    setValue('server_operating_systems', newSystems.length > 0 ? newSystems : []);
    setDeleteConfirmDialog({ open: false, index: null });

    // サービスの選択肢と入力値もクリア
    setEnvironmentOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[index];
      return newOptions;
    });
    setEnvironmentInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[index];
      return newValues;
    });

    // 削除に関連するエラーをクリア
    const serverOperatingSystemNameErrorKey = `server_operating_systems.${index}.server_operating_system_name`;
    const environmentNameErrorKey = `server_operating_systems.${index}.server_environment_name`;
    clearErrors(serverOperatingSystemNameErrorKey);
    clearErrors(environmentNameErrorKey);
  };

  // バリデーションエラーの取得
  const getFieldError = (index, fieldName) => {
    const fieldErrors = errors?.server_operating_systems;
    if (showValidation && fieldErrors && fieldErrors[index] && fieldErrors[index][fieldName]) {
      return fieldErrors[index][fieldName];
    }
    return null;
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && errors?.server_operating_systems) {
    server_operating_systems.forEach((_, index) => {
      const serverOperatingSystemNameError = getFieldError(index, 'server_operating_system_name');
      const environmentNameError = getFieldError(index, 'server_environment_name');
      if (serverOperatingSystemNameError) validationErrors.push(serverOperatingSystemNameError.message);
      if (environmentNameError) validationErrors.push(environmentNameError.message);
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        Server OS
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* サーバーOS項目の一覧 */}
      {server_operating_systems.length > 0 ? (
        server_operating_systems.map((system, index) => {
          const serverOperatingSystemNameError = getFieldError(index, 'server_operating_system_name');
          const environmentNameError = getFieldError(index, 'server_environment_name');

          return (
            <Box
              key={index}
              ref={el => formRefs.current[index] = el}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
              data-error={!!(serverOperatingSystemNameError || environmentNameError)}
            >
              {/* 削除ボタン */}
              <IconButton
                onClick={() => handleDeleteConfirm(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={3}>
                {/* カテゴリー */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>カテゴリー</Typography>
                    <Autocomplete
                      freeSolo
                      options={getFilteredServerOperatingSystemOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={system.server_operating_system_name || ''}
                      onChange={(event, newValue) => handleServerOperatingSystemChange(index, newValue)}
                      onInputChange={(event, newInputValue) => {
                        // 手動入力時の値を即座に反映
                        if (event && event.type === 'change') {
                          handleServerOperatingSystemChange(index, newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="カテゴリーを選択または入力してください"
                          variant="outlined"
                          error={!!serverOperatingSystemNameError}
                          helperText={serverOperatingSystemNameError?.message}
                          onBlur={(event) => {
                            // フォーカスアウト時に手動入力値を確定
                            const inputValue = event.target.value;
                            if (inputValue && inputValue !== system.server_operating_system_name) {
                              handleServerOperatingSystemChange(index, inputValue);
                            }
                          }}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* サービス */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>サービス（複数選択可）</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={getFilteredEnvironmentOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={system.server_environment_name ? system.server_environment_name.split(', ').filter(f => f.trim() !== '') : []}
                      inputValue={environmentInputValues[index] || ''}
                      onChange={(event, newValue) => handleEnvironmentChange(index, newValue)}
                      onInputChange={(event, newInputValue) => handleEnvironmentInputChange(index, newInputValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, tagIndex) => (
                          <Chip
                            variant="filled"
                            label={option}
                            {...getTagProps({ index: tagIndex })}
                            key={tagIndex}
                            sx={{
                              backgroundColor: '#4F46E5',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#f0f0f0'
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="サービスを選択または入力してください"
                          variant="outlined"
                          error={!!environmentNameError}
                          helperText={environmentNameError?.message}
                          onBlur={() => handleEnvironmentBlur(index)}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '2px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '56px',
                          padding: '8px 12px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        // すべてのフォームが削除された場合のメッセージ
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Server OSの項目を追加してください
          </Typography>
        </Box>
      )}

      {/* 追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Fab
          color="primary"
          onClick={handleAdd}
          sx={{
            bgcolor: '#4F46E5',
            '&:hover': {
              bgcolor: '#4338CA',
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="primary"
          >
            キャンセル
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmDialog.index)}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
